/**
 * PicGuard Modal — 20-point content safety check with animated scan
 * Ported from studio: self-contained modal DOM, uses workspace config
 */

import { CONFIG } from '../../core/config.js'
import { fetchWithTimeout, REQUEST_TIMEOUT_MS } from '../../api/index.js'
import { createLogger } from '../../utils/logger.js'
const log = createLogger('create-ui.picguard-modal')

const GATES = [
  {
    id: 1,
    name: 'Source Legitimacy',
    icon: '<path d="M12 2a5 5 0 015 5v1a5 5 0 01-10 0V7a5 5 0 015-5z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 2,
    name: 'IP Cleanliness',
    icon: '<path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 3,
    name: 'Content Safety',
    icon: '<path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
]

const POINTS = [
  {
    id: 1,
    gate: 1,
    name: 'TinEye Reverse Search',
    icon: '<circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="14.5" y1="14.5" x2="20" y2="20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="10" cy="10" r="2" fill="currentColor"/>',
  },
  {
    id: 2,
    gate: 1,
    name: 'Google Web Detection',
    icon: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M3 12h18M12 3c2.5 3 4 6 4 9s-1.5 6-4 9c-2.5-3-4-6-4-9s1.5-6 4-9z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 3,
    gate: 1,
    name: 'pHash Blacklist',
    icon: '<path d="M12 2C9 2 7 4 7 6c0 3 2 4 5 4s5-1 5-4c0-2-2-4-5-4zM7 14c-2 1-3 3-3 4v2h16v-2c0-1-1-3-3-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 4,
    gate: 1,
    name: 'URL Domain Analysis',
    icon: '<path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 5,
    gate: 1,
    name: 'EXIF Metadata',
    icon: '<rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="17" cy="8" r="1" fill="currentColor"/>',
  },
  {
    id: 6,
    gate: 2,
    name: 'Logo Detection',
    icon: '<path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 7,
    gate: 2,
    name: 'Celebrity Recognition',
    icon: '<path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: 8,
    gate: 2,
    name: 'IP Character Detection',
    icon: '<path d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 9,
    gate: 2,
    name: 'Text Detection',
    icon: '<path d="M3 5h18M3 5v2m18-2v2M7 5v14m0 0H5m2 0h2m4-14v14m0 0h-2m2 0h2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 10,
    gate: 2,
    name: 'Watermark Detection',
    icon: '<path d="M12 21a9 9 0 009-9c0-3-2-5.5-4-7.5S12 1 12 1s-3 1.5-5 4.5S3 9 3 12a9 9 0 009 9z" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  },
  {
    id: 11,
    gate: 2,
    name: 'Text Overlay',
    icon: '<rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 15h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 12,
    gate: 2,
    name: 'Stock Watermark',
    icon: '<rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 13,
    gate: 2,
    name: 'Font Licensing',
    icon: '<path d="M4 7V4h16v3M9 20h6M12 4v16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: 14,
    gate: 3,
    name: 'NSFW Detection',
    icon: '<path d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: 15,
    gate: 3,
    name: 'Violence Detection',
    icon: '<path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: 16,
    gate: 3,
    name: 'Drugs & Weapons',
    icon: '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="5.5" y1="5.5" x2="18.5" y2="18.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 17,
    gate: 3,
    name: 'Hate Symbols',
    icon: '<path d="M3 21V8l4-4 4 4v13M17 3v18m0-18l4 4m-4-4l-4 4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
  {
    id: 18,
    gate: 3,
    name: 'Minors Protection',
    icon: '<path d="M12 4.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zM6.5 21v-2a4 4 0 014-4h3a4 4 0 014 4v2" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 19,
    gate: 3,
    name: 'AI-Generated',
    icon: '<rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="9" cy="9" r="1" fill="currentColor"/><circle cx="15" cy="9" r="1" fill="currentColor"/><path d="M9 15h6" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>',
  },
  {
    id: 20,
    gate: 3,
    name: 'Text Safety',
    icon: '<path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
  },
]

// Inject CSS once
let cssInjected = false
function injectCSS() {
  if (cssInjected) return
  cssInjected = true
  const style = document.createElement('style')
  style.textContent = PG_CSS
  document.head.appendChild(style)
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms))

let modalEl = null

export function showPicGuardModal(imageUrl) {
  injectCSS()
  if (modalEl) modalEl.remove()

  const modalHTML = `
    <div class="pgm-modal">
      <button class="pgm-close" id="pgmClose">&times;</button>
      <div class="pgm-header">
        <div class="pgm-shield-wrap">
          <svg class="pgm-shield-svg" viewBox="0 0 24 24">
            <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" style="fill:var(--surface-s1);stroke:var(--border-strong)" stroke-width="0.5"/>
            <path class="pgm-shield-fill" d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" fill="url(#pgmShieldGrad)" style="clip-path:inset(100% 0 0 0)"/>
            <defs><linearGradient id="pgmShieldGrad" x1="0" y1="1" x2="0" y2="0"><stop offset="0%" stop-color="var(--state-success)" stop-opacity="0.6"/><stop offset="100%" stop-color="var(--brand-pillar-everywhere)" stop-opacity="0.3"/></linearGradient></defs>
          </svg>
          <div class="pgm-radar" id="pgmRadar"></div>
        </div>
        <div class="pgm-title">PicGuard AI</div>
        <div class="pgm-subtitle">20-Point Content Safety Score</div>
      </div>
      <div class="pgm-preview"><img id="pgmPreviewImg" src="${imageUrl}" onerror="this.style.display='none'"></div>
      <div class="pgm-progress">
        <div class="pgm-progress-info"><span id="pgmProgressText">Initializing...</span><span id="pgmProgressCount">0/20</span></div>
        <div class="pgm-progress-track"><div class="pgm-progress-bar" id="pgmProgressBar"></div></div>
      </div>
      <div id="pgmGates"></div>
      <div id="pgmResult"></div>
    </div>`

  modalEl = document.createElement('div')
  modalEl.className = 'pgm-overlay'
  modalEl.innerHTML = modalHTML
  document.body.appendChild(modalEl)
  requestAnimationFrame(() => modalEl.classList.add('show'))
  modalEl.querySelector('#pgmClose').onclick = closePicGuardModal
  modalEl.addEventListener('click', (e) => {
    if (e.target === modalEl) closePicGuardModal()
  })

  // Build gates
  const gatesEl = modalEl.querySelector('#pgmGates')
  gatesEl.innerHTML = GATES.map(
    (gate, gi) => `
    <div class="pgm-gate" style="transition-delay:${gi * 150}ms">
      <div class="pgm-gate-header">
        <svg class="pgm-gate-icon" viewBox="0 0 24 24">${gate.icon}</svg>
        <span>Gate ${gate.id}: ${gate.name}</span>
        <span class="pgm-gate-badge" data-gate="${gate.id}">PENDING</span>
      </div>
      ${POINTS.filter((p) => p.gate === gate.id)
        .map(
          (p) => `
        <div class="pgm-check" data-point="${p.id}">
          <div class="pgm-check-icon"><svg viewBox="0 0 24 24">${p.icon}</svg></div>
          <span class="pgm-check-name">${p.name}</span>
          <div class="pgm-check-status"><div class="pgm-dot"></div></div>
        </div>
      `
        )
        .join('')}
    </div>
  `
  ).join('')

  // Show gates
  setTimeout(
    () => modalEl.querySelectorAll('.pgm-gate').forEach((g) => g.classList.add('visible')),
    200
  )

  // Activate radar
  modalEl.querySelector('#pgmRadar').classList.add('active')

  // Run scan
  runScan(imageUrl)
}

export function closePicGuardModal() {
  if (modalEl) {
    modalEl.classList.remove('show')
    setTimeout(() => {
      if (modalEl) {
        modalEl.remove()
        modalEl = null
      }
    }, 300)
  }
}

async function runScan(imageUrl) {
  const animPromise = runScanAnimation()
  const apiPromise = callAPI(imageUrl)
  const [, result] = await Promise.all([animPromise, apiPromise])

  if (result) {
    await applyResults(result)
  } else {
    if (modalEl) modalEl.querySelector('#pgmProgressText').textContent = 'Detection failed'
    if (modalEl) modalEl.querySelector('#pgmRadar').classList.remove('active')
  }
}

async function runScanAnimation() {
  for (let i = 0; i < POINTS.length; i++) {
    if (!modalEl) return
    const el = modalEl.querySelector(`[data-point="${POINTS[i].id}"]`)
    if (!el) continue
    modalEl.querySelectorAll('.pgm-check.scanning').forEach((e) => {
      if (e !== el) e.classList.remove('scanning')
    })
    el.classList.add('scanning')
    const pct = Math.round(((i + 1) / POINTS.length) * 100)
    const bar = modalEl.querySelector('#pgmProgressBar')
    if (bar) bar.style.width = pct + '%'
    const countEl = modalEl.querySelector('#pgmProgressCount')
    if (countEl) countEl.textContent = `${i + 1}/20`
    const textEl = modalEl.querySelector('#pgmProgressText')
    if (textEl) textEl.textContent = `Scanning: ${POINTS[i].name}`
    const fill = modalEl.querySelector('.pgm-shield-fill')
    if (fill) fill.style.clipPath = `inset(${100 - pct}% 0 0 0)`
    await sleep(280)
  }
}

async function callAPI(imageUrl) {
  try {
    const body = imageUrl.startsWith('data:') ? { image_base64: imageUrl } : { image_url: imageUrl }
    const res = await fetchWithTimeout(
      `${CONFIG.PICGUARD_API}/api/v1/detect`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      REQUEST_TIMEOUT_MS.ANALYSIS
    )
    const data = await res.json()
    return data.success ? data.data : null
  } catch (e) {
    log.error('PicGuard API error:', e)
    return null
  }
}

async function applyResults(result) {
  if (!modalEl) return
  const points = result.points || []
  modalEl.querySelectorAll('.pgm-check.scanning').forEach((e) => e.classList.remove('scanning'))

  const pointMap = {}
  points.forEach((p) => {
    pointMap[p.id] = p
  })

  for (let i = 0; i < POINTS.length; i++) {
    if (!modalEl) return
    const p = POINTS[i]
    const el = modalEl.querySelector(`[data-point="${p.id}"]`)
    if (!el) continue
    const apiPoint = pointMap[p.id]
    const status = apiPoint ? apiPoint.status : 'pass'
    el.classList.remove('scanning')
    el.classList.add(status)
    const dot = el.querySelector('.pgm-dot')
    if (status === 'pass')
      dot.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/></svg>'
    else if (status === 'fail')
      dot.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>'
    else
      dot.innerHTML =
        '<svg width="10" height="10" viewBox="0 0 24 24"><path d="M12 9v4m0 4h.01" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>'
    await sleep(120)
  }

  // Gate badges
  GATES.forEach((gate) => {
    const gatePoints = points.filter((p) => {
      const d = POINTS.find((x) => x.id === p.id)
      return d && d.gate === gate.id
    })
    const allPass = gatePoints.every((p) => p.status === 'pass')
    const badge = modalEl.querySelector(`[data-gate="${gate.id}"]`)
    if (badge) {
      badge.textContent = allPass ? 'PASS' : 'ALERT'
      badge.className = 'pgm-gate-badge ' + (allPass ? 'pass' : 'fail')
    }
  })

  // Stop radar, update shield
  if (modalEl) modalEl.querySelector('#pgmRadar').classList.remove('active')
  const score = result.css_score || 0
  if (score >= 70 && modalEl) modalEl.querySelector('.pgm-shield-wrap').classList.add('safe')
  if (modalEl) modalEl.querySelector('#pgmProgressText').textContent = 'Scan complete'
  if (modalEl) modalEl.querySelector('#pgmProgressBar').style.width = '100%'

  await sleep(400)
  showResult(result)
}

function showResult(result) {
  if (!modalEl) return
  const score = result.css_score || 0
  const colorVar =
    score >= 70
      ? 'var(--state-success)'
      : score >= 50
        ? 'var(--state-warning)'
        : 'var(--state-danger)'
  const color = colorVar
  const decision =
    {
      pass: 'Commercially Safe',
      manual_review: 'Manual Review',
      force_remix: 'Remix Required',
      reject: 'Rejected',
    }[result.decision] || result.decision
  const circ = 2 * Math.PI * 44
  const offset = circ * (1 - score / 100)

  const section = modalEl.querySelector('#pgmResult')
  section.innerHTML = `
    <div class="pgm-result">
      <div class="pgm-score-wrap">
        <svg class="pgm-score-ring" width="110" height="110" style="transform:rotate(-90deg)">
          <circle cx="55" cy="55" r="44" fill="none" style="stroke:var(--border-subtle)" stroke-width="6"/>
          <circle cx="55" cy="55" r="44" fill="none" stroke="${color}" stroke-width="6"
            stroke-dasharray="${circ}" stroke-dashoffset="${circ}" stroke-linecap="round"
            style="transition:stroke-dashoffset 1.2s ease-out"/>
        </svg>
        <div class="pgm-score-text">
          <div style="font-size:32px;font-weight:700;color:${color}">${score}</div>
          <div style="font-size:10px;color:var(--gray-500);margin-top:2px">CSS Score</div>
        </div>
      </div>
      <div class="pgm-decision" style="color:${color}">${decision}</div>
      <div class="pgm-decision-sub">Detection ID: ${result.detection_id || 'N/A'}</div>
    </div>`
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      const circle = section.querySelector('circle:last-child')
      if (circle) circle.style.strokeDashoffset = offset
    })
  )
}

const PG_CSS = `
.pgm-overlay{position:fixed;inset:0;background:var(--bg-overlay);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);z-index:10000;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .3s}
.pgm-overlay.show{opacity:1}
.pgm-modal{background:var(--surface-bg);border:1px solid var(--border-subtle);border-radius:20px;width:100%;max-width:600px;max-height:85vh;overflow-y:auto;padding:32px;position:relative;box-shadow:0 0 80px color-mix(in oklch, var(--brand-pillar-everywhere) 8%, transparent);transform:translateY(20px) scale(.97);transition:transform .4s cubic-bezier(.16,1,.3,1);font-family:var(--font-body);color:var(--text-primary)}
.pgm-overlay.show .pgm-modal{transform:translateY(0) scale(1)}
.pgm-modal::-webkit-scrollbar{width:4px}
.pgm-modal::-webkit-scrollbar-thumb{background:var(--surface-s3);border-radius:2px}
.pgm-close{position:absolute;top:16px;right:16px;background:none;border:none;color:var(--gray-500);font-size:24px;cursor:pointer;width:32px;height:32px;display:flex;align-items:center;justify-content:center;border-radius:8px;transition:all .2s}
.pgm-close:hover{color:var(--text-primary);background:var(--surface-s2)}
.pgm-header{text-align:center;margin-bottom:24px}
.pgm-shield-wrap{position:relative;width:72px;height:72px;margin:0 auto 12px}
.pgm-shield-svg{width:72px;height:72px;filter:drop-shadow(0 0 12px color-mix(in oklch, var(--brand-pillar-everywhere) 40%, transparent))}
.pgm-shield-fill{transition:clip-path .6s ease}
.pgm-radar{position:absolute;inset:0;border-radius:50%;background:conic-gradient(from 0deg,transparent 0deg,color-mix(in oklch, var(--brand-pillar-everywhere) 25%, transparent) 60deg,transparent 120deg);animation:pgmRadar 2s linear infinite;opacity:0;transition:opacity .3s}
.pgm-radar.active{opacity:1}
@keyframes pgmRadar{from{transform:rotate(0)}to{transform:rotate(360deg)}}
.pgm-title{font-size:18px;font-weight:600;letter-spacing:.5px}
.pgm-subtitle{font-size:12px;color:var(--gray-500);margin-top:4px;font-weight:300}
.pgm-preview{text-align:center;margin-bottom:20px}
.pgm-preview img{width:80px;height:80px;object-fit:cover;border-radius:12px;border:1px solid var(--border-default);box-shadow:0 0 20px color-mix(in oklch, var(--brand-pillar-everywhere) 15%, transparent)}
.pgm-progress{margin-bottom:20px}
.pgm-progress-info{display:flex;justify-content:space-between;margin-bottom:6px;font-size:11px;color:var(--gray-500)}
.pgm-progress-track{height:2px;background:var(--surface-s2);border-radius:1px;overflow:hidden}
.pgm-progress-bar{height:100%;width:0%;background:linear-gradient(90deg,var(--brand-pillar-everywhere),var(--state-success));border-radius:1px;transition:width .3s ease}
.pgm-gate{margin-bottom:16px;border-radius:14px;background:var(--surface-s1);border:1px solid var(--border-subtle);overflow:hidden;opacity:0;transform:translateY(12px);transition:opacity .4s,transform .4s}
.pgm-gate.visible{opacity:1;transform:translateY(0)}
.pgm-gate-header{display:flex;align-items:center;gap:8px;padding:12px 16px;font-size:12px;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:.8px;border-bottom:1px solid var(--border-subtle)}
.pgm-gate-icon{width:16px;height:16px;opacity:.5}
.pgm-gate-badge{margin-left:auto;font-size:10px;padding:2px 8px;border-radius:6px;font-weight:600;background:var(--surface-s2);color:var(--gray-400);transition:all .4s}
.pgm-gate-badge.pass{background:color-mix(in oklch, var(--state-success) 12%, transparent);color:var(--state-success)}
.pgm-gate-badge.fail{background:color-mix(in oklch, var(--state-danger) 12%, transparent);color:var(--state-danger)}
.pgm-check{display:flex;align-items:center;gap:10px;padding:9px 16px;transition:all .3s;position:relative;overflow:hidden}
.pgm-check+.pgm-check{border-top:1px solid var(--border-subtle)}
.pgm-check-icon{width:18px;height:18px;flex-shrink:0;color:var(--gray-400);transition:color .3s}
.pgm-check-icon svg{width:18px;height:18px}
.pgm-check-name{flex:1;font-size:12px;color:var(--gray-500);transition:color .3s}
.pgm-check-status{width:18px;height:18px;flex-shrink:0;display:flex;align-items:center;justify-content:center}
.pgm-dot{width:6px;height:6px;border-radius:50%;background:var(--gray-300);transition:all .3s}
.pgm-check.scanning{background:color-mix(in oklch, var(--brand-pillar-everywhere) 4%, transparent)}
.pgm-check.scanning .pgm-check-icon{color:var(--brand-pillar-everywhere)}
.pgm-check.scanning .pgm-check-name{color:var(--text-secondary)}
.pgm-check.scanning .pgm-dot{width:8px;height:8px;background:var(--brand-pillar-everywhere);animation:pgmPulse 1s ease-in-out infinite;box-shadow:0 0 8px color-mix(in oklch, var(--brand-pillar-everywhere) 50%, transparent)}
.pgm-check.scanning::before{content:'';position:absolute;left:0;top:0;bottom:0;width:2px;background:var(--brand-pillar-everywhere);box-shadow:0 0 8px color-mix(in oklch, var(--brand-pillar-everywhere) 60%, transparent)}
.pgm-check.pass .pgm-check-icon{color:var(--state-success)}
.pgm-check.pass .pgm-check-name{color:var(--text-muted)}
.pgm-check.pass .pgm-dot{width:18px;height:18px;background:color-mix(in oklch, var(--state-success) 12%, transparent);color:var(--state-success);display:flex;align-items:center;justify-content:center}
.pgm-check.fail .pgm-check-icon{color:var(--state-danger)}
.pgm-check.fail .pgm-check-name{color:var(--text-muted)}
.pgm-check.fail .pgm-dot{width:18px;height:18px;background:color-mix(in oklch, var(--state-danger) 12%, transparent);color:var(--state-danger);display:flex;align-items:center;justify-content:center}
.pgm-check.warning .pgm-check-icon{color:var(--state-warning)}
.pgm-check.warning .pgm-check-name{color:var(--text-muted)}
.pgm-check.warning .pgm-dot{width:18px;height:18px;background:color-mix(in oklch, var(--state-warning) 12%, transparent);color:var(--state-warning);display:flex;align-items:center;justify-content:center}
@keyframes pgmPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.3)}}
.pgm-shield-wrap.safe{animation:pgmForceField 2s ease-in-out infinite}
@keyframes pgmForceField{0%,100%{filter:drop-shadow(0 0 12px color-mix(in oklch, var(--state-success) 30%, transparent))}50%{filter:drop-shadow(0 0 24px color-mix(in oklch, var(--state-success) 60%, transparent))}}
.pgm-result{text-align:center;padding:24px 0 8px;animation:pgmFadeUp .6s ease}
@keyframes pgmFadeUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
.pgm-score-wrap{position:relative;width:110px;height:110px;margin:0 auto 24px}
.pgm-score-ring{display:block}
.pgm-score-text{position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center}
.pgm-decision{font-size:14px;font-weight:600;margin-bottom:4px}
.pgm-decision-sub{font-size:11px;color:var(--gray-500)}
`
